import { useState, useEffect } from "react";
import { httpPost } from "../../../../../Utils/apis/apis";
import axios from "axios";
import { getSubDomain } from "../../../Pages/Common/User Management/HelperFunctions";
import { containerType } from "../../../../../Utils/config/config";
import {
  DOCUMENTS_UI_STRINGS,
  FILE_TYPE_BADGE_STYLES,
} from "./DocumentsDashboard.constants";

export const validateCategoryName = (name) => {
  if (!name || !name.trim()) return null;
  if (!/^[a-zA-Z0-9_\s-]+$/.test(name))
    return DOCUMENTS_UI_STRINGS.CATEGORY_SPECIAL_CHARS_ERROR;
  if (name !== name.trim())
    return DOCUMENTS_UI_STRINGS.CATEGORY_LEADING_TRAILING_SPACE_ERROR;
  if (name.startsWith("."))
    return DOCUMENTS_UI_STRINGS.CATEGORY_DOT_START_ERROR;
  if (/\s{2,}/.test(name))
    return DOCUMENTS_UI_STRINGS.CATEGORY_CONSECUTIVE_SPACES_ERROR;
  if (name.trim().length > 63)
    return DOCUMENTS_UI_STRINGS.CATEGORY_MAX_LENGTH_ERROR;
  return null;
};

export const getFileCategories = async (spName) => {
  try {
    const payload = [
      {
        id: "GetCategories",
        spName: spName,
        parameters: {},
      },
    ];
    const response = await httpPost(
      `/SQLQuery/getDataFromMultipleSPs?chartSPs=false`,
      payload,
      {},
      true
    );
    return response.status === 200 ? response?.data?.GetCategories : [];
  } catch (error) {
    console.error("Error fetching file categories:", error);
    throw new Error("Failed to fetch file categories");
  }
};

export const addFileCategory = async (spName, categoryName) => {
  try {
    const payload = [
      {
        id: "AddCategory",
        spName: spName,
        parameters: {
          FileCategoryName: categoryName,
        },
      },
    ];
    const response = await httpPost(
      `/SQLQuery/getDataFromMultipleSPs?chartSPs=false`,
      payload,
      {},
      true
    );
    return response.status === 200 ? response?.data?.AddCategory : [];
  } catch (error) {
    console.error("Error adding file category:", error);
    throw new Error("Failed to add file category");
  }
};

// Note: httpPost never rejects — on failure it resolves with the axios error
// object itself (see Utils/apis/apis.js), so failures are detected by status,
// not by catching a throw.
export const renameFileCategory = async (oldFullPath, newFullPath) => {
  const response = await httpPost(
    `/SQLQuery/rename-file-category`,
    { oldFullPath, newFullPath },
    {},
    true
  );
  if (response?.status === 200) return true;
  throw new Error(
    response?.response?.data?.Message || "Failed to rename folder"
  );
};

export const deleteFileCategory = async (fullPath) => {
  const response = await httpPost(
    `/SQLQuery/delete-file-category`,
    { fullPath },
    {},
    true
  );
  if (response?.status === 200) return true;
  throw new Error(
    response?.response?.data?.Message || "Failed to delete folder"
  );
};

// ─── Folder path helpers ─────────────────────────────────────────────────────

export const getCategoryFullPath = (cat) =>
  String(
    cat?.FileCategoryName || cat?.categoryName || cat?.CategoryName || ""
  ).trim();

// Segment-safe containment check: "Legal" must match "Legal/Signed" but never "Legalese".
export const isPathWithin = (candidatePath, ancestorPath) =>
  candidatePath === ancestorPath ||
  candidatePath.startsWith(`${ancestorPath}/`);

export const getFolderDepth = (fullPath) => fullPath.split("/").length;

export const getParentPath = (fullPath) => {
  const idx = fullPath.lastIndexOf("/");
  return idx === -1 ? null : fullPath.slice(0, idx);
};

export const getLeafName = (fullPath) => {
  const idx = fullPath.lastIndexOf("/");
  return idx === -1 ? fullPath : fullPath.slice(idx + 1);
};

// Builds a nested folder tree from the flat list of category rows returned by
// getFileCategories, where each row's name is a full "/"-joined path
// (e.g. "Legal", "Legal/Signed", "Legal/Signed/2024"). Every tree node
// corresponds to a real row — no synthesized intermediate nodes are created.
export const buildFolderTree = (categories) => {
  const nodeByPath = new Map();
  const roots = [];

  const rows = categories
    .map((cat) => ({
      fullPath: getCategoryFullPath(cat),
      isSystemGenerated: !!cat.IsSystemGenerated,
    }))
    .filter((row) => row.fullPath)
    .sort((a, b) => getFolderDepth(a.fullPath) - getFolderDepth(b.fullPath));

  rows.forEach(({ fullPath, isSystemGenerated }) => {
    const node = {
      name: getLeafName(fullPath),
      fullPath,
      isSystemGenerated,
      children: [],
    };
    nodeByPath.set(fullPath, node);

    const parentPath = getParentPath(fullPath);
    const parentNode = parentPath ? nodeByPath.get(parentPath) : null;
    if (parentNode) {
      parentNode.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortChildren = (node) => {
    node.children.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );
    node.children.forEach(sortChildren);
  };
  roots.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  );
  roots.forEach(sortChildren);

  return roots;
};

// Flattens a folder tree into a depth-ordered list for use in indented
// dropdowns (Move/Upload category selects), e.g. [{ fullPath, depth }, ...].
export const flattenFolderTree = (nodes, depth = 0, acc = []) => {
  nodes.forEach((node) => {
    acc.push({
      fullPath: node.fullPath,
      name: node.name,
      depth,
      isSystemGenerated: node.isSystemGenerated,
    });
    flattenFolderTree(node.children, depth + 1, acc);
  });
  return acc;
};

// Prunes a folder tree down to nodes whose own name matches `term`, or that
// have a descendant which does — so the path to a match stays visible.
export const filterFolderTree = (nodes, term) => {
  const normalized = term.trim().toLowerCase();
  if (!normalized) return nodes;

  const filterLevel = (levelNodes) =>
    levelNodes
      .map((node) => {
        const filteredChildren = filterLevel(node.children);
        const selfMatches = node.name.toLowerCase().includes(normalized);
        if (!selfMatches && !filteredChildren.length) return null;
        return { ...node, children: filteredChildren };
      })
      .filter(Boolean);

  return filterLevel(nodes);
};

// Finds the tree node for a given folder path, so its direct children
// (immediate sub-folders) can be rendered as navigable rows.
export const findFolderNode = (nodes, fullPath) => {
  for (const node of nodes) {
    if (node.fullPath === fullPath) return node;
    const found = findFolderNode(node.children, fullPath);
    if (found) return found;
  }
  return null;
};

// `container` selects which blob container the file lands in -- default "generic" (this loan's
// documents), but a container-backed restricted folder such as Sales Tracker uploads into its
// own container instead (see VirtualFolderTargets.js). An empty `category` means "no folder
// segment", which is how those containers key their blobs; joining it in unconditionally would
// produce a "{crmId}//{fileName}" path with an empty segment.
export const uploadDocument = async (file, fileName, category, crmId, user, container = containerType.GENERIC) => {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async (e) => {
      try {
        const subdomain = getSubDomain();
        const finalFileName = category ? `${crmId}/${category}/${fileName}` : `${crmId}/${fileName}`;
        const url = process.env.DOCUMENT_HELPER;
        const header = {
          filename: finalFileName,
          type: container,
          "file-operation": "upload",
          "process-file": "no",
          "x-functions-key": process.env.DOCUMENT_HELPER_KEY,
          subdomain: subdomain,
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + user.access_token,
        };
        const formData = {
          file: e.target.result,
        };
        const response = await axios.post(url, formData, { headers: header });
        resolve(response.data);
      } catch (error) {
        console.error("Error uploading document:", error);
        reject(error);
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

export const downloadDocument = async (
  fileKey,
  user,
  containerType = "generic"
) => {
  try {
    const subdomain = getSubDomain();
    const url = `${process.env.DOCUMENT_HELPER}`;
    const headers = {
      filename: fileKey,
      type: containerType,
      "file-operation": "download",
      "process-file": "yes",
      "x-functions-key": process.env.DOCUMENT_HELPER_KEY,
      subdomain,
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "Bearer " + user.access_token,
    };
    const response = await httpPost(url, "", {
      responseType: "blob",
      headers,
    });
    if (!response) return;
    const blobUrl = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", fileKey.split("/").pop());
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Document download failed:", err);
    throw new Error("Failed to download document.");
  }
};

// Lifted verbatim (header shape and `file-operation: "delete"` contract) from the
// `handleFileDelete` block in M_fileUpload.helper.js, so the app has one place that knows how to
// delete a Document Helper blob instead of a fourth copy of the same header block.
export const deleteDocument = async (fileKey, user, containerType = "generic") => {
  try {
    const subdomain = getSubDomain();
    const url = `${process.env.DOCUMENT_HELPER}`;
    const headers = {
      filename: fileKey,
      type: containerType,
      "file-operation": "delete",
      "process-file": "no",
      "x-functions-key": process.env.DOCUMENT_HELPER_KEY,
      subdomain,
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "Bearer " + user.access_token,
    };
    const response = await axios.post(url, {}, { headers });
    return response.data;
  } catch (err) {
    console.error("Document delete failed:", err);
    throw new Error("Failed to delete document.");
  }
};

export const moveDocument = async (
  oldFileKey,
  newFileKey,
  user,
  oldCategory,
  newCategory
) => {
  try {
    const subdomain = getSubDomain();
    const url = `${process.env.DOCUMENT_HELPER}`;
    const headers = {
      filename: newFileKey,
      "old-filename": oldFileKey,
      "old-category": oldCategory,
      "new-category": newCategory,
      type: "generic",
      "file-operation": "move",
      "process-file": "no",
      "x-functions-key": process.env.DOCUMENT_HELPER_KEY,
      subdomain,
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "Bearer " + user.access_token,
    };

    const response = await axios.post(url, {}, { headers });
    return response.data;
  } catch (err) {
    console.error("Document move failed:", err);
    throw new Error("Failed to move document.");
  }
};

// Keeps blob storage in sync with a folder rename recorded in SQL — moves
// every document physically stored under the old folder path to the new one.
// rename_folder_prefix reports a partially applied folder rename as 207 Multi-Status, with a JSON
// body naming the files it could not move. Axios resolves every 2xx, so a 207 used to look like a
// clean success: the folder row moved in SQL, some blobs stayed under the old prefix, and the user
// was told it worked. Folder counts are derived from blob paths while the folder tree comes from
// SQL, so that leftover split is exactly what shows up as a wrong file count.
const PARTIAL_RENAME_STATUS = 207;

// The function sends the body as a JSON string without a JSON content type, so axios hands it over
// unparsed.
const parseRenameResult = (data) => {
  if (!data) return null;
  if (typeof data === "object") return data;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
};

export const renameFolderDocuments = async (
  oldFullPath,
  newFullPath,
  crmId,
  user
) => {
  let response;
  try {
    const subdomain = getSubDomain();
    const url = `${process.env.DOCUMENT_HELPER}`;
    const headers = {
      filename: `${crmId}/${newFullPath}`,
      "old-filename": `${crmId}/${oldFullPath}`,
      "old-category": oldFullPath,
      type: "generic",
      "file-operation": "rename-folder",
      "process-file": "no",
      "x-functions-key": process.env.DOCUMENT_HELPER_KEY,
      subdomain,
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "Bearer " + user.access_token,
    };
    response = await axios.post(url, {}, { headers });
  } catch (err) {
    console.error("Folder document rename failed:", err);
    throw new Error("Folder renamed, but some documents may not have moved.");
  }

  if (response?.status === PARTIAL_RENAME_STATUS) {
    const result = parseRenameResult(response.data);
    const movedCount = result?.movedCount ?? 0;
    const failedCount = result?.failed?.length ?? 0;
    console.error("Folder document rename partially failed:", result);
    throw new Error(
      `Moved ${movedCount} of ${movedCount + failedCount} file(s). ${failedCount} ${DOCUMENTS_UI_STRINGS.FOLDER_DOCS_PARTIAL_MOVE}`
    );
  }

  return response.data;
};

export const fetchCategoryDocuments = async (
  fileKey,
  user,
  containerType = "generic"
) => {
  try {
    const subdomain = getSubDomain();
    const url = `${process.env.DOCUMENT_HELPER}`;
    const headers = {
      filename: fileKey,
      type: containerType,
      "file-operation": "blob-items",
      "process-file": "yes",
      "x-functions-key": process.env.DOCUMENT_HELPER_KEY,
      subdomain,
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "Bearer " + user.access_token,
    };
    const response = await axios.post(url, {}, { headers });
    return response.data;
  } catch (err) {
    console.error("Document fetch failed:", err);
    return [];
  }
};

export const mapBlobItemsToDocuments = (
  rawDocs,
  { category, containerType = "generic" } = {}
) => {
  if (!rawDocs || !rawDocs.length) return [];
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  return rawDocs.map((d, i) => {
    let fileStr = d.file ? d.file.replace(/\\/g, "/").replace(/^\/+/, "") : "";
    const parts = fileStr.split("/");
    const fileNamePart = parts.pop() || "Unknown";
    const folderParts = parts.filter((p) => !uuidRegex.test(p));

    let cat = category || DOCUMENTS_UI_STRINGS.UNCATEGORIZED;
    let fname;

    if (category) {
      fname = folderParts.length
        ? safeDecode(folderParts.join("/") + "/" + fileNamePart)
        : safeDecode(fileNamePart);
    } else if (folderParts.length >= 1) {
      // The full, "/"-joined folder path is the category — e.g. "Legal/Signed/2024" —
      // not just the first segment, so nested folders round-trip correctly.
      cat = safeDecode(folderParts.join("/")).trim();
      fname = safeDecode(fileNamePart);
    } else {
      fname = safeDecode(fileNamePart);
    }

    return {
      id: `${containerType}-${i}`,
      rawFile: d.file,
      filename: fname,
      category: cat,
      containerType,
      uploadedOn: d.creationTime || d.modifiedTime,
      uploadedBy: d.uploadedBy || "System",
      ext: fname.split(".").pop().toLowerCase(),
    };
  });
};

// ─── UI Utility Functions ────────────────────────────────────────────────────

export const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

export const normalizeCat = (str) =>
  String(str).toLowerCase().replace(/\s+/g, "");

export const safeDecode = (str) => {
  try {
    return decodeURIComponent(str);
  } catch {
    return str;
  }
};

export const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
};

export const getUploadedOnTime = (dateStr) => {
  if (!dateStr) return 0;
  const time = new Date(dateStr).getTime();
  return Number.isNaN(time) ? 0 : time;
};

export const getFilterChipStyle = (ext, isActive) => {
  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-mode") === "dark";

  const palette = {
    PDF: {
      backgroundColor: "#fbf1de",
      color: "#d09a37",
      borderColor: "#f0ddba",
    },
    XLSX: {
      backgroundColor: "#edf6e6",
      color: "#86ac53",
      borderColor: "#dceccf",
    },
    XLS: {
      backgroundColor: "#edf6e6",
      color: "#86ac53",
      borderColor: "#dceccf",
    },
    DOCX: {
      backgroundColor: "#e8f1fb",
      color: "#79a9db",
      borderColor: "#d9e7f8",
    },
    DOC: {
      backgroundColor: "#e8f1fb",
      color: "#79a9db",
      borderColor: "#d9e7f8",
    },
    PPTX: {
      backgroundColor: "#f8ebea",
      color: "#d5a09a",
      borderColor: "#f1dad8",
    },
    PPT: {
      backgroundColor: "#f8ebea",
      color: "#d5a09a",
      borderColor: "#f1dad8",
    },
    CSV: {
      backgroundColor: "#e6f6f4",
      color: "#7ebcb6",
      borderColor: "#d3eeea",
    },
    PNG: {
      backgroundColor: "#f0ebfb",
      color: "#b3a2e6",
      borderColor: "#e3dbf8",
    },
    SVG: {
      backgroundColor: "#f5f5f5",
      color: "#71717a",
      borderColor: "#e4e4e7",
    },
  };

  const darkPalette = {
    PDF: {
      backgroundColor: "rgba(208, 154, 55, 0.15)",
      color: "#f8d695",
      borderColor: "rgba(208, 154, 55, 0.3)",
    },
    XLSX: {
      backgroundColor: "rgba(134, 172, 83, 0.15)",
      color: "#b9d695",
      borderColor: "rgba(134, 172, 83, 0.3)",
    },
    XLS: {
      backgroundColor: "rgba(134, 172, 83, 0.15)",
      color: "#b9d695",
      borderColor: "rgba(134, 172, 83, 0.3)",
    },
    DOCX: {
      backgroundColor: "rgba(121, 169, 219, 0.15)",
      color: "#b8d5f5",
      borderColor: "rgba(121, 169, 219, 0.3)",
    },
    DOC: {
      backgroundColor: "rgba(121, 169, 219, 0.15)",
      color: "#b8d5f5",
      borderColor: "rgba(121, 169, 219, 0.3)",
    },
    PPTX: {
      backgroundColor: "rgba(213, 160, 154, 0.15)",
      color: "#f1ccc8",
      borderColor: "rgba(213, 160, 154, 0.3)",
    },
    PPT: {
      backgroundColor: "rgba(213, 160, 154, 0.15)",
      color: "#f1ccc8",
      borderColor: "rgba(213, 160, 154, 0.3)",
    },
    CSV: {
      backgroundColor: "rgba(126, 188, 182, 0.15)",
      color: "#b8dfdb",
      borderColor: "rgba(126, 188, 182, 0.3)",
    },
    PNG: {
      backgroundColor: "rgba(179, 162, 230, 0.15)",
      color: "#d8cff5",
      borderColor: "rgba(179, 162, 230, 0.3)",
    },
    SVG: {
      backgroundColor: "rgba(161, 161, 170, 0.15)",
      color: "#e4e4e7",
      borderColor: "rgba(161, 161, 170, 0.3)",
    },
  };

  const selectedPalette = isDark ? darkPalette : palette;
  const base =
    selectedPalette[ext] ||
    (isDark
      ? {
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          color: "var(--color-text-secondary)",
          borderColor: "rgba(128, 128, 128, 0.2)",
        }
      : {
          backgroundColor: "#f2eee6",
          color: "#8a8173",
          borderColor: "#e4dccc",
        });

  return {
    ...base,
    border: `1px solid ${base.borderColor}`,
    opacity: isActive ? 1 : 0.6,
  };
};

export const getInitials = (name) => {
  if (!name) return "U";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

export const getFileBadgeClass = (ext) =>
  FILE_TYPE_BADGE_STYLES[ext] || FILE_TYPE_BADGE_STYLES.default;

export const getDocumentFileName = (fileKey) => {
  const parts = String(fileKey || "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean);
  return parts[parts.length - 1] || "";
};

export const getMovedFileKey = (fileKey, targetCategory, crmId) =>
  `${crmId}/${targetCategory}/${getDocumentFileName(fileKey)}`;
